import React, {useCallback, useContext, useEffect, useState, useRef} from 'react';
import {List, Map} from 'immutable';
import classnames from 'classnames';
import {
  List as VirtualizedList,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache
} from 'react-virtualized';
import {debounce} from 'lodash';
import {easeInOutCirc} from 'js-easing-functions';
import {Icon, ListGroup, ListGroupItem, LoadingSpinner, Text} from '@albert-io/atomic';

import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

import QuestionsListFooter from 'components/PracticeView/QuestionsList/QuestionsListFooter.react';
import {MobilePracticeViewContext} from 'components/PracticeView/MobilePracticeViewProvider.context';

import getDifficultyValueFromInt from 'lib/getDifficultyValueFromInt';

import {Question, QuestionList, Guess} from './ViewStudentSubmission.types';

import './view-student-submission.scss';

interface Props {
  activeQuestion: Question;
  scrollToQuestionByIdFunc: any;
  onQuestionClick: (args: any) => () => void;
  questionSets: List<QuestionList>;
  paginatedResultsManager: any;
  guesses: Map<string, Guess>;
}

const getGuessIcon = (guess) => {
  if (guess === 'correct' || guess === 'complete') {
    return 'check';
  }
  if (guess === 'incorrect') {
    return 'times';
  }
  return 'minus';
};

const VSSQuestionList = ({
  activeQuestion,
  scrollToQuestionByIdFunc,
  onQuestionClick,
  questionSets,
  paginatedResultsManager,
  guesses
}: Props) => {
  const {isMobile, isQuestionsListInView, setIsQuestionsListInView} =
    useContext(MobilePracticeViewContext);
  const cellMeasurerCache = useRef<CellMeasurerCache>();
  const listRef = useRef<VirtualizedList>(null);
  const scrollPosition = useRef(0);
  const [isLoading, setIsLoading] = useState(false);

  const smoothScrollToPosition = useCallback(
    (endPos, startPos = scrollPosition.current, startTime = Date.now()) => {
      const duration = 1000;
      const elapsed = Date.now() - startTime;
      const nextPos = easeInOutCirc(elapsed, startPos, endPos - startPos, duration);
      // @ts-ignore
      listRef.current.scrollToPosition(nextPos);
      if (elapsed < duration) {
        global.requestAnimationFrame(() => {
          smoothScrollToPosition(endPos, startPos, startTime);
        });
      }
    },
    []
  );

  useEffect(() => {
    cellMeasurerCache.current = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 0
    });
  }, []);

  useEffect(() => {
    const scrollToQuestion = (questionSetId) => {
      const index = questionSets.findIndex((questionSet) => questionSet!.getId() === questionSetId);
      if (index === undefined) {
        return;
      }
      const offset = listRef!.current!.getOffsetForRow({
        alignment: 'start',
        index
      });
      smoothScrollToPosition(offset);
    };

    scrollToQuestionByIdFunc.current = scrollToQuestion;
  }, [scrollToQuestionByIdFunc, questionSets, smoothScrollToPosition]);

  const handleRowsRendered = debounce(async ({stopIndex}) => {
    const isLastQuestionScrolledIntoView = stopIndex + 1 === questionSets.size;
    if (!paginatedResultsManager.isRequestInFlight) {
      if (isLastQuestionScrolledIntoView) {
        setIsLoading(true);
        await paginatedResultsManager.fetchNextPage();
        setIsLoading(false);
      }
    }
  }, 250);

  const updateScrollPosition = ({scrollTop}) => {
    scrollPosition.current = scrollTop;
  };

  const rowRenderer = ({index, key, parent, style}) => {
    const questionSet = questionSets.get(index);
    let guessResult =
      guesses.get(questionSet?.getQuestions().get(0).getId())?.get('result') || 'n/a';

    const questionType = questionSet?.getQuestions().get(0).getQuestionType();
    if (guessResult === 'correct' && questionType === 'free-response') {
      guessResult = 'complete';
    }

    return (
      <CellMeasurer
        cache={cellMeasurerCache.current!}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}
      >
        <ListGroupItem
          id={questionSet?.getId()}
          as='button'
          tabIndex={0}
          onClick={() => {
            const onQuestionClickFunction = onQuestionClick({
              questionSet,
              question: questionSet?.getQuestions().first()
            });
            onQuestionClickFunction();
            if (isMobile && isQuestionsListInView) {
              setIsQuestionsListInView(false);
            }
          }}
          style={style}
          className='question-v2 u-cursor_pointer u-display_flex u-justify-content_space-between u-align-items_center'
        >
          <div className='u-display_flex u-flex-direction_column u-text-align_left u-flex-grow_1'>
            <Text
              as={MarkdownRendererV2}
              // @ts-ignore Text has no property text, but MarkdownRendererV2 does so ignore error
              text={questionSet?.getQuestions().get(0).getTitle()}
              size='m'
            />
            <Text size='xs' emphasis='lowest' className='u-text-transform_capitalize u-mar-t_1'>
              {getDifficultyValueFromInt(questionSet?.getQuestions().get(0).getDifficulty(), true)}
            </Text>
          </div>
          <div
            className='u-display_flex u-flex-wrap_no-wrap u-align-items_center u-justify-content_flex-end'
            style={{minWidth: 115}}
          >
            <div
              style={{padding: '4px 8px'}}
              className={classnames(
                {
                  'u-bgc_green-050 u-color_green-700':
                    guessResult === 'correct' || guessResult === 'complete',
                  'u-bgc_red-050 u-color_red-600': guessResult === 'incorrect',
                  'u-bgc_slate-050 u-color_slate-600': guessResult === 'n/a'
                },
                'u-display_flex u-align-items_center u-justify-content_center u-border-radius_1'
              )}
            >
              <Icon icon={getGuessIcon(guessResult)} />
              <Text size='s' color='inherit' bold className='u-text-transform_capitalize u-mar-l_1'>
                {guessResult}
              </Text>
            </div>
          </div>
          <div
            className={classnames(
              {
                'clickable-indicator--active':
                  questionSet?.getId() === activeQuestion.getQuestionSet().getId()
              },
              'clickable-indicator'
            )}
          />
        </ListGroupItem>
      </CellMeasurer>
    );
  };

  return (
    <div
      className={classnames(
        'practice-view__questions-wrapper vss-questions-list_wrapper u-display_flex u-overflow_hidden u-pad-b_2 u-pad-r_2 u-pad-t_2',
        {
          'u-pointer-events_none': isMobile && !isQuestionsListInView
        }
      )}
    >
      <ListGroup
        className={classnames(
          'questions-list-v2 practice-view__questions-list vss-questions-list u-display_flex u-flex-direction_column',
          {
            'practice-view__questions-list--in-view': isQuestionsListInView
          }
        )}
      >
        <ListGroupItem header className='u-display_flex u-justify-content_space-between'>
          <Text bold>Question Name</Text>
          <Text bold>Result</Text>
        </ListGroupItem>
        <div className='u-flex-grow_1'>
          <AutoSizer>
            {({width, height}) => (
              <VirtualizedList
                ref={listRef}
                height={height}
                width={width}
                rowHeight={cellMeasurerCache.current?.rowHeight || 100}
                onRowsRendered={handleRowsRendered}
                rowCount={questionSets.size}
                rowRenderer={rowRenderer}
                onScroll={updateScrollPosition}
                tabIndex={-1}
              />
            )}
          </AutoSizer>
          {isLoading ? <LoadingSpinner className='vss-student-dropdown__loader' size={2} /> : null}
        </div>
        <ListGroupItem footer paddingLevel={0} className='questions-list-v2__footer'>
          <QuestionsListFooter
            activeQuestion={activeQuestion}
            setActiveQuestionState={(question, questionSet) => {
              const onQuestionClickFunction = onQuestionClick({question, questionSet});
              onQuestionClickFunction();
            }}
            scrollToQuestionById={scrollToQuestionByIdFunc}
            paginatedQuestionsListManager={paginatedResultsManager}
          />
        </ListGroupItem>
      </ListGroup>
    </div>
  );
};

export default VSSQuestionList;
